import type { ReactNode } from "react";

import { formatDay } from "@/lib/format";

import { Button } from "./Button";
import { Dialog, DialogFooter } from "./Dialog";
import styles from "./ConfirmReleaseLoss.module.css";

/** What happens to the release, in the same words wherever it is asked. */
const CONSEQUENCE =
  "Mit dieser Änderung wird die Freigabe ungültig — danach neu rechnen und neu freigeben.";

/**
 * The question a facade gesture asks on a released Vorgang (spec 2026-10-15
 * §4), as one sentence — for the dialog below and for a caller that asks it
 * inside a dialog of its own („Form ändern …“, Task 7).
 *
 * `by === null` is a release whose author the server no longer names; `at
 * === null` a release this screen has not loaded yet — the question is asked
 * all the same, because the server will take the release away whether or not
 * the screen could say whose it was.
 */
export function releaseLossSentence(by: string | null, at: string | null): string {
  if (at === null) return `Der Vorgang ist freigegeben. ${CONSEQUENCE}`;
  const who = by === null ? "" : ` von ${by}`;
  return `Freigegeben${who} am ${formatDay(at)}. ${CONSEQUENCE}`;
}

interface ConfirmReleaseLossProps {
  /** `ReviewOut.by` — the display name of whoever released it. */
  by: string | null;
  /** `ReviewOut.at`. */
  at: string | null;
  /** What is about to change („Wand 3 auf 5.000 mm“), under the title. */
  subtitle?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  /** Once the dialog is gone and the focus back with its opener (`Dialog.onClosed`). */
  onClosed?: () => void;
}

/**
 * „Trotzdem ändern“ or „Abbrechen“ — before a gesture that moves the Stand
 * of a released Vorgang (`quote_approved`). The server resets the Vorgang to
 * „In Planung“ on that gesture and the calculation goes stale (D-11/E-52);
 * this only makes sure nobody learns that afterwards.
 *
 * The focus lands on „Abbrechen“: it stands first in the footer, and the safe
 * way out is the one an Enter pressed out of habit should take.
 */
export function ConfirmReleaseLoss({ by, at, subtitle, onConfirm, onCancel, onClosed }: ConfirmReleaseLossProps) {
  return (
    <Dialog title="Freigabe wird ungültig" subtitle={subtitle} onClose={onCancel} onClosed={onClosed}>
      <p className={styles.text}>{releaseLossSentence(by, at)}</p>
      <DialogFooter>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button variant="primary" onClick={onConfirm}>
          Trotzdem ändern
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
